import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Close, RestartAlt, Search } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { useFormContext } from 'react-hook-form';
import { date, object } from 'yup';
import { getNotificationTypeOptions } from 'api/notifications';
import { getToken } from 'Auth';
import MuiButton from 'components/Common/Button/MuiButton';
import {
    AutocompleteWithController,
    DatePickerWithController,
    TextFieldWithController,
} from 'components/Common/Form/MuiInputs';
import NotificationFilter from 'interfaces/NotificationFilter';
import './stylesheets/NotificationFilters.scss';
import SelectOption from 'interfaces/SelectOption';

const filtersSchema = object().shape(
    {
        fromCreatedAt: date().when('toCreatedAt', ([value], schema) =>
            DateTime.fromJSDate(value).isValid
                ? schema.test(
                      'is-before-to-date',
                      'From Date should be before To Date',
                      (value, context) =>
                          value ? value < context.parent.toCreatedAt : true
                  )
                : schema
        ),
        toCreatedAt: date().when('fromCreatedAt', ([value], schema) =>
            DateTime.fromJSDate(value).isValid
                ? schema.test(
                      'is-after-from-date',
                      'To Date should be after From Date',
                      (value, context) =>
                          value ? value > context.parent.fromCreatedAt : true
                  )
                : schema
        ),
    },
    [
        ['fromCreatedAt', 'toCreatedAt'],
        ['toCreatedAt', 'fromCreatedAt'],
    ]
);

export type NotificationFiltersProps = {
    onSearch: (filters: NotificationFilter) => Promise<void>;
    disableSearch?: boolean;
};

const NotificationFilters = ({
    onSearch,
    disableSearch = false,
}: NotificationFiltersProps) => {
    const [notificationTypeOptions, setNotificationTypeOptions] = useState<
        SelectOption[]
    >([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { handleSubmit, reset, watch, setValue } = useFormContext();
    const { instance } = useMsal();

    const searchTerm = watch('searchTerm');

    useEffect(() => {
        getToken(instance)
            .then((token) => {
                setIsLoading(true);
                return getNotificationTypeOptions(token);
            })
            .then((data) => setNotificationTypeOptions(data))
            .finally(() => setIsLoading(false));
    }, []);

    return (
        <form onSubmit={handleSubmit(onSearch)}>
            <div id="notification-filters" data-testid="filters">
                <div
                    className="notification-filters-input"
                    data-testid="fromCreatedAt"
                >
                    <DatePickerWithController
                        controllerProps={{ name: 'fromCreatedAt' }}
                        datePickerProps={{
                            label: 'From Created Date',
                            views: ['year', 'month', 'day'],
                            timezone: 'UTC',
                        }}
                        errorAsPopper
                    />
                </div>
                <div
                    className="notification-filters-input"
                    data-testid="toCreatedAt"
                >
                    <DatePickerWithController
                        controllerProps={{ name: 'toCreatedAt' }}
                        datePickerProps={{
                            label: 'To Created Date',
                            views: ['year', 'month', 'day'],
                            timezone: 'UTC',
                        }}
                        errorAsPopper
                    />
                </div>
                <div
                    className="notification-filters-input"
                    style={{ width: '300px' }}
                    data-testid="notificationType"
                >
                    <AutocompleteWithController
                        controllerProps={{ name: 'notificationType' }}
                        label="Notification Type"
                        autocompleteProps={{
                            options: notificationTypeOptions,
                            disabled: isLoading,
                        }}
                    />
                </div>
                <div className="notification-filters-input">
                    <TextFieldWithController
                        controllerProps={{ name: 'searchTerm' }}
                        textFieldProps={{
                            label: 'Search Term',
                            inputProps: { 'data-testid': 'searchTerm' },
                            InputProps: {
                                endAdornment: (
                                    <IconButton
                                        onClick={() =>
                                            setValue('searchTerm', undefined)
                                        }
                                        sx={{
                                            visibility: !isEmpty(searchTerm)
                                                ? 'visible'
                                                : 'hidden',
                                        }}
                                    >
                                        <Close />
                                    </IconButton>
                                ),
                                sx: { paddingRight: 0 },
                            },
                        }}
                    />
                </div>
                <div className="d-flex gap-3">
                    <MuiButton
                        variant="contained"
                        onClick={() => reset()}
                        sx={{ gap: '0.4rem' }}
                        data-testid="reset"
                    >
                        <RestartAlt sx={{ color: 'white' }} />
                        <span>Reset</span>
                    </MuiButton>
                    <MuiButton
                        variant="contained"
                        type="submit"
                        sx={{ gap: '0.4rem' }}
                        disabled={isLoading || disableSearch}
                        data-testid="search"
                    >
                        <Search sx={{ color: 'white' }} />
                        <span>Search</span>
                    </MuiButton>
                </div>
            </div>
        </form>
    );
};
export { NotificationFilters as default, filtersSchema };
